import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Select,
  InputNumber,
  Alert,
  App,
} from 'antd';
import { PlusOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/redux';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const AddBrandPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector(state => state.auth);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { t } = useTranslation();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const role = (user as any)?.role;
    if (role !== 'admin') {
      message.error(t('addbrand.admin_only_error'));
      navigate('/brands');
      return;
    }
  }, [user, navigate]);

  const categories = [
    'fashion',
    'accessories',
    'leather goods',
    'jewelry',
    'watches',
    'cosmetics',
  ];

  const onFinish = async (values: any) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/brands', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (data.success) {
        message.success(t('addbrand.submit_success'));
        navigate('/brands');
      } else {
        message.error(data.error || t('addbrand.submit_failed'));
      }
    } catch (error) {
      message.error(t('addbrand.network_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Title level={2}>{t('addbrand.title')}</Title>

      <Card>
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Form.Item
            name="name"
            label={t('addbrand.name_label')}
            rules={[{ required: true, message: t('addbrand.name_required') }]}
          >
            <Input placeholder={t('addbrand.name_placeholder')} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('addbrand.description_label')}
            rules={[
              { required: true, message: t('addbrand.description_required') },
            ]}
          >
            <TextArea
              rows={4}
              placeholder={t('addbrand.description_placeholder')}
            />
          </Form.Item>

          <Form.Item
            name="founded"
            label={t('addbrand.founded_label')}
            rules={[
              { required: true, message: t('addbrand.founded_required') },
            ]}
          >
            <InputNumber
              min={1800}
              max={new Date().getFullYear()}
              placeholder={t('addbrand.founded_placeholder')}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="headquarters"
            label={t('addbrand.hq_label')}
            rules={[{ required: true, message: t('addbrand.hq_required') }]}
          >
            <Input placeholder={t('addbrand.hq_placeholder')} />
          </Form.Item>

          <Form.Item
            name="website"
            label={t('addbrand.website_label')}
            rules={[
              { required: true, message: t('addbrand.website_required') },
              { type: 'url', message: t('addbrand.website_invalid') },
            ]}
          >
            <Input placeholder={t('addbrand.website_placeholder')} />
          </Form.Item>

          <Form.Item
            name="categories"
            label={t('addbrand.categories_label')}
            rules={[
              { required: true, message: t('addbrand.categories_required') },
            ]}
          >
            <Select
              mode="multiple"
              placeholder={t('addbrand.categories_placeholder')}
              style={{ width: '100%' }}
            >
              {categories.map(category => (
                <Option key={category} value={category}>
                  {category}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<PlusOutlined />}
              size="large"
            >
              {t('addbrand.submit_btn')}
            </Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => navigate('/brands')}
            >
              {t('addbrand.cancel_btn')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default AddBrandPage;
